// PreloadScene — two-pass manifest loader (RESEARCH Pattern 8).
//
// Pass 1: load `/assets/manifest.json` only.
// Pass 2: iterate the manifest and queue per-asset loads. Re-call
// `this.load.start()` to drive the second pass; on COMPLETE again,
// transition to MenuScene.
//
// Phase 1 ships an empty manifest (placeholders rendered procedurally
// via Phaser primitives). Phase 3 populates images/spritesheets/audio
// from the Retro-Diffusion + fal.ai pipelines — zero loader code change.

import Phaser from 'phaser';
import { TestSeam } from '../debug/TestSeam';

interface ManifestImage {
  key: string;
  path: string;
}
interface ManifestSpriteSheet {
  key: string;
  path: string;
  frameWidth: number;
  frameHeight: number;
  spacing?: number;
  margin?: number;
}
interface ManifestBitmapFont {
  key: string;
  texturePath: string;
  dataPath: string;
}
interface ManifestAudio {
  key: string;
  paths: string[];
}
interface Manifest {
  version: 1;
  images: ManifestImage[];
  spritesheets: ManifestSpriteSheet[];
  bitmapFonts: ManifestBitmapFont[];
  audio: ManifestAudio[];
}

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super('PreloadScene');
  }

  create(): void {
    TestSeam.setScene('PreloadScene');
    this.load.json('manifest', '/assets/manifest.json');
    this.load.once(Phaser.Loader.Events.COMPLETE, () => this.afterManifest());
    this.load.start();
  }

  private afterManifest(): void {
    const m = this.cache.json.get('manifest') as Manifest | undefined;
    if (!m) {
      // Manifest missing — still proceed to MenuScene so dev boot doesn't hang.
      console.warn('[PreloadScene] manifest.json not found at /assets/manifest.json');
      this.scene.start('MenuScene');
      return;
    }

    for (const img of m.images) {
      this.load.image(img.key, img.path);
    }
    for (const sh of m.spritesheets) {
      this.load.spritesheet(sh.key, sh.path, {
        frameWidth: sh.frameWidth,
        frameHeight: sh.frameHeight,
        spacing: sh.spacing,
        margin: sh.margin,
      });
    }
    for (const bf of m.bitmapFonts) {
      this.load.bitmapFont(bf.key, bf.texturePath, bf.dataPath);
    }
    for (const au of m.audio) {
      this.load.audio(au.key, au.paths);
    }

    // Second-pass terminal COMPLETE. With an empty manifest there is
    // nothing queued; Phaser still fires COMPLETE next tick.
    this.load.once(Phaser.Loader.Events.COMPLETE, () => this.scene.start('MenuScene'));
    this.load.start();
  }
}
